import { Moment } from 'moment'

import { ValidInvalidTagValues } from 'Shared/atoms'

import { ConfigItem, DefaultFile } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

import { CreateContactFormData } from './Contact'

export enum CertificateSearchResultFields {
	hesSupplierIds = 'hesSupplierIds',
	hisSupplierIds = 'hisSupplierIds',
	hesSupplierNames = 'hesSupplierNames',
	hisSupplierNames = 'hisSupplierNames',
	insuredNames = 'insuredNames'
}

export interface CertificateSearchSuggestions {
	[CertificateSearchResultFields.hesSupplierIds]: Entity['id'][]
	[CertificateSearchResultFields.hisSupplierIds]: Entity['id'][]
	[CertificateSearchResultFields.hesSupplierNames]: Entity[]
	[CertificateSearchResultFields.hisSupplierNames]: Entity[]
	[CertificateSearchResultFields.insuredNames]: Entity[]
}

export enum CertificateFilterFields {
	isHesSupplier = 'isHesSupplier',
	isValidCompliance = 'isValidCompliance',
	supplierGroupFilter = 'supplierGroupFilter',
	classCodeFilter = 'classCodeFilter'
}

export type CertificateValidInvalidSections =
	| 'generalLiabilities'
	| 'autoLiabilities'
	| 'excessUmbrellaLiabilities'
	| 'workersLiabilities'
	| 'otherLiabilities'

export enum BaseValidationLiabilityLabels {
	ProducerId = 'producerId',
	PolicyNumber = 'policyNumber',
	PolicyExpirationDate = 'policyExpirationDate',
	state = 'states',
	supplierInsuranceTypeId = 'supplierInsuranceTypeId'
}

export enum BaseLiabilityLabels {
	ProducerId = 'producerId',
	PolicyNumber = 'policyNumber',
	PolicyExpirationDate = 'policyExpirationDate',
	IsActive = 'isActive',
	IsPerContract = 'isPerContract',
	IsCoverageChangeApproved = 'isCoverageChangeApproved',
	CoverageChangeNotes = 'coverageChangeNotes',
	PolicyDescription = 'policyDescription'
}

export enum ComplianceValidationLiabilityLabels {
	OccurrenceAmount = 'occurrenceAmount',
	AggregateAmount = 'aggregateAmount',
	IsAdditionalInsured = 'isAdditionalInsured',
	IsOngoingAndCompetedOperations = 'isOngoingAndCompetedOperations',
	IsPrimaryNonContributory = 'isPrimaryNonContributory',
	IsSubrogationWaiver = 'isSubrogationWaiver',
	IsAggregatePerProjectAndPerLocation = 'isAggregatePerProjectAndPerLocation',
	NoExclusion = 'noExclusion'
}

export enum GeneralLiabilityLabels {
	Insurer = 'insurer',
	ProductAmount = 'productAmount',
	Producer = 'producer'
}

export interface ComplianceValidation {
	valid: boolean
	required: boolean
	rule: number | boolean
	less?: boolean
	defaultValue?: boolean | string | number | object
	classCode: number
}

export type ComplianceValidationConfig = Record<string, ComplianceValidation>

export type StoreLiabilityValue =
	| 'excessUmbrellaLiability'
	| 'autoLiability'
	| 'generalLiability'
	| 'workersLiability'
	| 'otherLiability'
export type StoreLiabilityListValue = CertificateValidInvalidSections

export declare namespace Certificate {
	interface Create {
		hisSupplierId?: number
		hesSupplierId?: number
		isContractExecuted?: boolean
		supplyChainStatus: number | null
		insuredName: string
		hesGroups: number[]
		additionalNotes: string | null
	}
	interface FormattedDocuments {
		documentId: Key
		isActive: boolean
	}

	interface Update extends Omit<Create, 'additionalNotes'> {}

	namespace Form {
		interface Create {
			activeTab: 'hisSupplier' | 'hesSupplier'
			additionalNotes: string | null
			hesGroup: LabelValue<number>[]
			insuredName: string
			classCodeTier: string | null
			supplyChainStatus: LabelValue | null
			contractExecuted: 'yes' | 'no' | 'na'
			supplier: LabelValue | null
		}

		interface Edit extends Omit<Create, 'additionalNotes' | 'activeTab' | 'supplier'> {
			supplier: LabelValue
			dataEntryComplete: boolean
			supplierActiveStatus: boolean
		}
		interface Documents {
			documents: FormattedDocuments[]
		}
	}
	interface Compliance {
		compliance: ValidInvalidTagValues
	}

	interface Basic extends Compliance {
		id: number
		supplier: Entity
		supplierType: string
		insuredName: string
		classCode: string
		lastUpdateDate: string | null
		isDataEntryComplete: boolean
		notes: string
	}

	interface Details extends Compliance {
		id: number
		supplier: Entity
		supplierType: string
		insuredName: string
		classCode: string
		lastUpdateDate: string
		isDataEntryComplete: boolean
		hesGroups: Entity[]
		isContractExecuted: boolean
		supplyChainStatus: Entity
		isSupplierActive: boolean
	}
	interface Documents {
		id: number
		documentId: number
		documentName: string
		createDate: string
		isActive: boolean
		isVisible?: boolean
	}

	interface Notes {
		notes: string
		userName: string
		createDate: string
		userFullName: string
	}

	namespace Notes {
		interface Create {
			notes: string
		}

		interface Form {
			notes: string | null
		}
	}

	interface ClassCode {
		code: string
		name: string
	}

	namespace Search {
		interface Config {
			[CertificateSearchResultFields.hesSupplierIds]: ConfigItem<CertificateSearchResultFields>
			[CertificateSearchResultFields.hisSupplierIds]: ConfigItem<CertificateSearchResultFields>
			[CertificateSearchResultFields.hesSupplierNames]: ConfigItem<CertificateSearchResultFields>
			[CertificateSearchResultFields.hisSupplierNames]: ConfigItem<CertificateSearchResultFields>
			[CertificateSearchResultFields.insuredNames]: ConfigItem<CertificateSearchResultFields>
		}

		interface Result {
			[CertificateSearchResultFields.hesSupplierIds]: string[]
			[CertificateSearchResultFields.hisSupplierIds]: string[]
			[CertificateSearchResultFields.hesSupplierNames]: string[]
			[CertificateSearchResultFields.hisSupplierNames]: string[]
			[CertificateSearchResultFields.insuredNames]: string[]
		}
	}
	namespace Filter {
		interface Config {
			[CertificateFilterFields.isHesSupplier]: ConfigFilterItem<string, string>
			[CertificateFilterFields.isValidCompliance]: ConfigFilterItem<string, string>
			[CertificateFilterFields.supplierGroupFilter]: ConfigFilterItem<string, string>
			[CertificateFilterFields.classCodeFilter]: ConfigFilterItem<string, string>
		}
	}
	interface AddDocumentFormData {
		document: DefaultFile | null
	}

	interface AddDocument {
		document: File | null
	}

	namespace Contact {
		interface FlagFields {
			isActive?: boolean
			isContactByEmail: boolean
			isContactByPhone: boolean
			isNationalContact: boolean
			isMainContact: boolean
			isInsuranceContact: boolean
		}
		interface Basic extends FlagFields, Entity {
			contactType?: string
			contactId: number
			email: string
			officePhone: string
			mobilePhone: string
		}
		interface Detailed
			extends Omit<Request.Create, 'supplierContactTypeId' | 'preferredLanguageId'> {
			id: number
			hesContactId: number
			preferredLanguage: Entity
			supplierContactType: Entity
		}

		namespace Request {
			interface Add extends FlagFields {
				contactId: string
			}
			interface Create extends FlagFields {
				supplierContactTypeId?: number
				firstName: string
				lastName: string
				emailAddress: string
				preferredLanguageId: number
				prefix: string | null
				mi: string | null
				suffix: string | null
				mailCode: string | null
				pin: string | null
				fax: string | null
				employer: string | null
				internalEmployee: string | null
				jobTitle: string | null
				officePhone: string | null
				mobilePhone: string | null
				emergencyPhone: string | null
				billingAgencyIds?: number[]
			}
		}
		namespace Form {
			interface Add extends Omit<Request.Add, 'contactId'> {
				contactId: LabelValue | null
			}

			interface Create extends FlagFields, CreateContactFormData {}
		}
	}

	namespace Producer {
		interface Contact {
			id?: Key
			contactName: string | null
			email: string | null
			phone: string | null
			isActive: boolean
		}

		interface Basic {
			id: number
			producerId: number
			producerName: string
			contactName: string
			phone: string
			email: string
			isActive: boolean
			isContactActive: boolean
		}

		interface SingleProducer {
			producerName: string
			insuranceProducerId: Key
			isActive: boolean
			setInsuranceCoverage: boolean
			assignExistingCoverage: boolean
			contacts: Contact[]
		}

		namespace Form {
			interface Add {
				insuranceProducerId: Key
				setActiveProducer: boolean
				setInsuranceCoverage: boolean
			}

			interface Edit extends Add {
				id?: Key
				assignExistingCoverage: boolean
				producerName: string
				contacts: Contact[]
			}

			interface Create {
				producerName: string
				isActive: boolean
				contacts: Contact[]
			}
		}

		interface FormData {
			insuranceProducerId: LabelValue
			producerName: string
			setActiveProducer: boolean
			setInsuranceCoverage: boolean
			assignExistingCoverage: boolean
			contacts: Contact[]
		}

		namespace Request {
			interface Create extends Form.Create {}
			interface Add extends Form.Add {}
			interface Edit extends Form.Edit {}
		}

		interface SearchOptions extends LabelValue {
			contacts: Contact[]
		}

		interface SearchProducer {
			id: number
			producerName: string
			contacts: Contact[]
		}
	}
	interface BasicItem {
		id: number
		producerName: string
		policyExpirationDate: string
		isActive: boolean
		policyNumber: string
	}
	namespace GeneralLiability {
		interface Basic extends Compliance, BasicItem {
			aggregateAmount: number
			occurrenceAmount: number
			productAmount: number
		}
		interface SingleGeneralLiability {
			id: Key
			[GeneralLiabilityLabels.Producer]: Entity
			[BaseLiabilityLabels.PolicyNumber]: string
			[BaseLiabilityLabels.PolicyExpirationDate]: string | null
			[BaseLiabilityLabels.IsPerContract]: boolean
			[BaseLiabilityLabels.PolicyDescription]: string
			[GeneralLiabilityLabels.Insurer]: string
			[ComplianceValidationLiabilityLabels.OccurrenceAmount]: number | null
			[ComplianceValidationLiabilityLabels.AggregateAmount]: number | null
			[GeneralLiabilityLabels.ProductAmount]: number | null
			[ComplianceValidationLiabilityLabels.IsAdditionalInsured]: boolean
			[ComplianceValidationLiabilityLabels.IsOngoingAndCompetedOperations]: boolean
			[ComplianceValidationLiabilityLabels.IsPrimaryNonContributory]: boolean
			[ComplianceValidationLiabilityLabels.IsSubrogationWaiver]: boolean
			[BaseLiabilityLabels.IsActive]: boolean
			[ComplianceValidationLiabilityLabels.IsAggregatePerProjectAndPerLocation]: boolean
			[ComplianceValidationLiabilityLabels.NoExclusion]: boolean
			[BaseLiabilityLabels.IsCoverageChangeApproved]: boolean
			[BaseLiabilityLabels.CoverageChangeNotes]: string
		}

		namespace Form {
			interface Add extends Omit<SingleGeneralLiability, 'producer'> {
				[BaseLiabilityLabels.ProducerId]: Key
			}
		}

		interface FormData
			extends Omit<
				Form.Add,
				| BaseLiabilityLabels.ProducerId
				| BaseLiabilityLabels.PolicyExpirationDate
				| ComplianceValidationLiabilityLabels.OccurrenceAmount
				| ComplianceValidationLiabilityLabels.AggregateAmount
				| GeneralLiabilityLabels.ProductAmount
				| 'id'
			> {
			[BaseLiabilityLabels.ProducerId]: LabelValue | null
			[BaseLiabilityLabels.PolicyExpirationDate]: Moment | null
			[ComplianceValidationLiabilityLabels.OccurrenceAmount]: string | null
			[ComplianceValidationLiabilityLabels.AggregateAmount]: string | null
			[GeneralLiabilityLabels.ProductAmount]: string | null
		}

		interface Request extends Form.Add {}
	}

	namespace AutoLiability {
		interface Basic extends Compliance, BasicItem {
			eachAccidentAmount: number
			generalAggregateAmount: number
			hazHaulerPollutionAmount: number
		}
		interface SingleAutoLiability {
			id: Key
			[BaseLiabilityLabels.PolicyNumber]: string
			[BaseLiabilityLabels.PolicyExpirationDate]: string
			eachAccidentAmount: number
			generalAggregateAmount: number
			hazHaulerPollutionAmount: number
			[BaseLiabilityLabels.IsActive]: boolean
			[BaseLiabilityLabels.IsPerContract]: boolean
			[BaseLiabilityLabels.PolicyDescription]: string
			isOwned: boolean
			isNonOwned: boolean
			isHired: boolean
			isScheduled: boolean
			insurer: string
			isAdditionalInsured: boolean
			isOngoingAndCompetedOperations: boolean
			isPrimaryNonContributory: boolean
			isSubrogationWaiver: boolean
			isMcs90: boolean
			isCoverageIncluds: boolean
			[BaseLiabilityLabels.IsCoverageChangeApproved]: boolean
			[BaseLiabilityLabels.CoverageChangeNotes]: string
			producer: Entity
		}

		namespace Form {
			interface Add extends Omit<SingleAutoLiability, 'producer'> {
				[BaseLiabilityLabels.ProducerId]: Key
			}
		}

		interface FormData
			extends Omit<
				Form.Add,
				| BaseLiabilityLabels.ProducerId
				| BaseLiabilityLabels.PolicyExpirationDate
				| 'id'
				| 'eachAccidentAmount'
				| 'generalAggregateAmount'
				| 'hazHaulerPollutionAmount'
			> {
			[BaseLiabilityLabels.ProducerId]: LabelValue | null
			[BaseLiabilityLabels.PolicyExpirationDate]: Moment | null
			eachAccidentAmount: string | null
			generalAggregateAmount: string | null
			hazHaulerPollutionAmount: string | null
		}

		interface Request extends Form.Add {}
	}
	namespace ExcessUmbrellaLiability {
		interface Basic extends Compliance, BasicItem {
			aggregateAmount: number | string
			occurrenceAmount: number | string
		}

		interface SingleExcessUmbrellaLiability {
			id: Key
			producer: Entity
			[BaseLiabilityLabels.PolicyNumber]: string
			[BaseLiabilityLabels.PolicyExpirationDate]: string
			[BaseLiabilityLabels.IsActive]: boolean
			[BaseLiabilityLabels.IsPerContract]: boolean
			[BaseLiabilityLabels.PolicyDescription]: string
			isWcCover: boolean
			isCglCover: boolean
			isAlCover: boolean
			isPlCover: boolean
			isPllCover: boolean
			isCplCover: boolean
			insurer: string
			eachOccurence: number
			aggregate: number
			isAdditionalInsured: boolean
			isOngoingAndCompetedOperations: boolean
			isPrimaryNonContributory: boolean
			isSubrogationWaiver: boolean
			[BaseLiabilityLabels.IsCoverageChangeApproved]: boolean
			[BaseLiabilityLabels.CoverageChangeNotes]: string
		}

		namespace Form {
			interface Add extends Omit<SingleExcessUmbrellaLiability, 'producer'> {
				[BaseLiabilityLabels.ProducerId]: Key
			}
		}

		interface FormData
			extends Omit<
				Form.Add,
				| BaseLiabilityLabels.ProducerId
				| BaseLiabilityLabels.PolicyExpirationDate
				| 'id'
				| 'eachOccurence'
				| 'aggregate'
			> {
			[BaseLiabilityLabels.ProducerId]: LabelValue | null
			[BaseLiabilityLabels.PolicyExpirationDate]: Moment | null
			eachOccurence: string | null
			aggregate: string | null
		}

		interface Request extends Form.Add {}
	}

	namespace WorkersLiability {
		interface Basic extends Compliance, BasicItem {
			accidentAmount: number
			diseaseEmployeeAmount: number
			diseasePolicyAmount: number
			states: string[]
		}
		interface SingleWorkersLiability {
			id: Key
			[BaseLiabilityLabels.PolicyNumber]: string
			[BaseLiabilityLabels.PolicyExpirationDate]: string
			[BaseLiabilityLabels.IsActive]: boolean
			[BaseLiabilityLabels.IsPerContract]: boolean
			[BaseLiabilityLabels.PolicyDescription]: string
			insurer: string
			isSubrogationWaiver: boolean
			[BaseLiabilityLabels.IsCoverageChangeApproved]: boolean
			[BaseLiabilityLabels.CoverageChangeNotes]: string
			accidentAmount: number
			diseasePolicyAmount: number
			diseaseEmployeeAmount: number
			isStatutory: boolean
			isCoverageApplicable: boolean
			isAlternativeEmployerEndorsement: boolean
			states: Entity[]
			producer: Entity
		}

		namespace Form {
			interface Add extends Omit<SingleWorkersLiability, 'producer' | 'states'> {
				[BaseLiabilityLabels.ProducerId]: Key
				states: number[]
			}
		}

		interface FormData
			extends Omit<
				Form.Add,
				| BaseLiabilityLabels.ProducerId
				| BaseLiabilityLabels.PolicyExpirationDate
				| 'id'
				| 'accidentAmount'
				| 'diseasePolicyAmount'
				| 'diseaseEmployeeAmount'
				| 'states'
			> {
			[BaseLiabilityLabels.ProducerId]: LabelValue | null
			[BaseLiabilityLabels.PolicyExpirationDate]: Moment | null
			accidentAmount: string | null
			diseasePolicyAmount: string | null
			diseaseEmployeeAmount: string | null
			states: LabelValue<number>[]
		}

		interface Request extends Form.Add {}
	}

	namespace OtherLiability {
		interface Basic extends Compliance, BasicItem {
			insuranceType: string
			occurrenceAmount: number
			aggregateAmount: number
		}

		interface SingleOtherLiability {
			id: Key
			[BaseLiabilityLabels.PolicyNumber]: string
			[BaseLiabilityLabels.PolicyExpirationDate]: string
			[BaseLiabilityLabels.IsActive]: boolean
			[BaseLiabilityLabels.IsPerContract]: boolean
			[BaseLiabilityLabels.PolicyDescription]: string
			insurer: string
			supplierInsuranceType: Entity
			occurrenceAmount: number
			aggregateAmount: number
			[ComplianceValidationLiabilityLabels.IsAdditionalInsured]: boolean
			[ComplianceValidationLiabilityLabels.IsOngoingAndCompetedOperations]: boolean
			[ComplianceValidationLiabilityLabels.IsPrimaryNonContributory]: boolean
			[ComplianceValidationLiabilityLabels.IsSubrogationWaiver]: boolean
			isCoverageIncluds: boolean
			[BaseLiabilityLabels.IsCoverageChangeApproved]: boolean
			[BaseLiabilityLabels.CoverageChangeNotes]: string
			producer: Entity
		}

		namespace Form {
			interface Add extends Omit<SingleOtherLiability, 'producer' | 'supplierInsuranceType'> {
				[BaseLiabilityLabels.ProducerId]: Key
				supplierInsuranceTypeId: Key | null
			}
		}

		interface FormData
			extends Omit<
				Form.Add,
				| BaseLiabilityLabels.ProducerId
				| BaseLiabilityLabels.PolicyExpirationDate
				| 'id'
				| 'occurrenceAmount'
				| 'aggregateAmount'
			> {
			[BaseLiabilityLabels.ProducerId]: LabelValue | null
			[BaseLiabilityLabels.PolicyExpirationDate]: Moment | null
			occurrenceAmount: string | null
			aggregateAmount: string | null
		}

		interface Request extends Form.Add {}
	}
}
